import UnleashService from '@/services/unleash'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import UnleashFlagTurnOnTime from '@/constant/UnleashFlagTurnOnTime'
import officeConstants from '@/constant/office'
import { canReadSlideTags } from '@/services/office/slide-tags'
import { isBulkSlideAddSupported } from '@/services/office/insert-slides'
import { isPptDialog } from '@/business-logic/ppt-dialog'
import PresentationApi from '@/api/presentation'
import { calculateBillingCycleNumber } from '@/utils/billing.utils'
import { WORKSPACE_APP_ID } from '@/constant/workspaceBack'
import { CONVERT_CONTENT_V1_TO_V2_FLAG } from '@/constant/contentV2Convert'
import { coalesce } from '@/utils/promise'
import { ROUTE_NAMES, PUBLIC_ROUTES } from '@/constant/routes'
import {
  MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG,
  DISCOUNT_VARIANT_NAME_PREFIX,
  MONTHLY_CANCEL_YEARLY_OFFER_FLAG,
  CANCELLATION_OFFER_TREATMENT_VARIANT,
} from '@/constant/monthlyToYearlyUpgrade'
import { flags } from '@/utils/abtest.utils'
import { getUnleashOverride, applyUnleashOverrides } from '@/utils/unleashOverride.util'
import {
  NEW_SLIDE_MARKETPLACE_FLAG,
  NEW_SLIDE_MARKETPLACE_VARIANTS,
  DATA_DRIVEN_SLIDE_PICKER_FLAG,
} from '@/constant/newSlideMarketplaceExperiment.constant'
import { SLIDE_PICKER_V5_FLAG } from '@/constant/slidePickerV5.constant'
import {
  MY_FILES_MENU_FLAG,
  MY_FILES_MENU_VARIANTS,
} from '@/constant/myFilesMenuExperiment.constant'
import {
  WORKSPACE_TRASH_FLAG,
  WORKSPACE_TRASH_VARIANTS,
} from '@/constant/workspaceTrashExperiment.constant'
import {
  LESSON_MENU_FLAG,
  KNOWLEDGE_BASE_MENU_FLAG,
  COURSE_MENU_FLAG,
  ADDON_MENU_VARIANTS,
} from '@/constant/myFilesAddonMenus.constant'
import {
  WORKSPACE_SHARED_FLAG,
  WORKSPACE_SHARED_VARIANTS,
} from '@/constant/workspaceSharedExperiment.constant'

import UserApi from '@/api/user'
import { normalizeRegisteredAt } from '@/util/monthlyParticipant'

const { unleashClient } = UnleashService

const { canUseNewUnleashMechanism, canUseCacheProfileOnLoad } = flags

// The Unleash SDK emits an 'error' event for every failed toggle fetch, which
// includes benign, self-healing network failures. On /pages/login the app
// rapidly switches context (anonymous -> authenticated), aborting in-flight
// requests, and many users are on flaky/blocked networks. Those surface as
// `TypeError: Failed to fetch` / `AbortError` and are non-actionable — the SDK
// retries and emits 'recovered'. We must not report them to Sentry (see
// STPANCRAS-PRESENTER-APP-13N4). Genuine `{ type: 'HttpError', code }` errors
// are not TypeErrors and carry no "failed to fetch" message, so they pass through.
const isBenignUnleashError = (error) => {
  if (!error) return true
  // In-flight request aborted by a context change / navigation
  if (error.name === 'AbortError' || error === 'AbortError') return true
  // Network-layer fetch failure: offline, DNS, CORS, connection reset, blocked.
  // Cross-browser messages: Chrome "Failed to fetch", Firefox "NetworkError
  // when attempting to fetch resource.", Safari "Load failed", Node "fetch failed".
  let msg = ''
  if (typeof error === 'string') {
    msg = error
  } else if (typeof error.message === 'string') {
    msg = error.message
  }
  return /failed to fetch|fetch failed|network ?error|load failed/i.test(msg)
}

// BOO-58 / AHA-43262: free-user monthly-vs-yearly direct-to-Stripe billing-cycle A/B test.
const DIRECT_FEATURE_UPGRADE_FLAG = 'experiment-direct-feature-upgrade'

// AHA-46375: PowerPoint add-in full-bleed resize. Read twice below (enabled + variant).
const PPT_AUTO_RESIZE_FLAG = 'AHA-46375-ppt-auto-resize'

// BOO-180 / AHA-46496: gates the editor side-nav custom-branding upsell card.
const CUSTOM_BRANDING_BANNER_FLAG = 'AHA-46496-custom-branding-banner'

// AHAM-638: lets a marketplace slide-type iframe persist the host slide title
// (slide.title) through the existing updateSlide xprop. Default OFF — a title
// write is a new persist path, so production behaviour is unchanged until this
// is enabled. See canWriteSlideTitleFromMarketplace + slidePluginTitleWriteMixin
// (read by both the canvas and settings plugin-iframe hosts).
const MARKETPLACE_SLIDE_TITLE_WRITE_FLAG = 'AHAM-638-marketplace-slide-title-write'

const REDITUS_REFERRAL_FLAG = 'release-AHA-40676-reditus-referral'
// Case-sensitive: must equal the Unleash variant name exactly, or the treatment arm never matches.
const REDITUS_REFERRAL_TREATMENT_VARIANT = 'treatment'

const REFER_EARN_HOME_CARD_FLAG = 'release-AHA-40676-refer-earn-home-card'
const REFER_EARN_HOME_CARD_TREATMENT_VARIANT = 'treatment'

// The variant lists platforms in human terms ("web"), not as Office.js PlatformType values
// ('OfficeOnline'), so normalise before comparing. An unknown token matches no platform.
const PPT_VARIANT_TOKEN_TO_PLATFORM = {
  pc: officeConstants.PC,
  mac: officeConstants.MAC,
  web: officeConstants.OFFICE_ONLINE,
  officeonline: officeConstants.OFFICE_ONLINE,
}

// The variant payload is a JSON array of platform tokens. Returns null when nothing usable
// is configured, which callers read as "all platforms".
const parsePptAutoResizePlatforms = (variant) => {
  const rawPlatforms = variant?.payload?.value
  if (!rawPlatforms) return null
  try {
    const platforms = JSON.parse(rawPlatforms)
    if (!Array.isArray(platforms) || !platforms.length) return null
    return platforms
  } catch (error) {
    return null
  }
}

// Slide types whose generate-options support is fully rolled out (flags retired).
// `categorise` is intentionally excluded — it is still gated behind
// `release-generate-answers-categorise` (not fully rolled out yet).
const GENERATE_OPTIONS_ROLLED_OUT_SLIDE_TYPES = [
  'pickAnswer',
  'pickImage',
  'typeAnswer',
  'matchPairs',
  'correctOrder',
  'poll',
]

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
  // anonymous is without any auth
  // routes: Sharing View, Login View, Register View
  hasLoadedAnonymousFlag: false,
  // other route that requires auth
  hasLoadedAuthFlag: false,
  // featureFlagInitCoalesced is used to prevent multiple feature flag init
  featureFlagInitCoalesced: null,
  // SYST-74: flag-info for the share code being opened ({ shareCode, createdAt,
  // resetTimeStamp }) — the only deck metadata available before the deck is fetched, so
  // the share-path endpoint choice is answered from it. Kept off `state.presentation` (a
  // pre-load value there is indistinguishable from "loaded") and carries its share code so
  // the getter can refuse a different deck's entry. See PR #5108.
  presentationFlagInfoByShareCode: null,
}

let isStartedUnleash = false

// AHA-42358: how long any feature may wait for user-scoped flags to load before
// giving up. On timeout the caller proceeds with flags-not-ready (the safe
// default for flag-gated code, e.g. plan benefit falls back to the legacy
// endpoint) rather than hanging forever when Unleash is slow/unavailable.
const DEFAULT_FLAG_READY_TIMEOUT_MS = 2000

// Reusable "user flags are loaded" gate. Resolves once setAuthFlag(true) fires,
// i.e. after updateContext has fetched the authenticated user's flags. Features
// that must read a flag immediately on app load (which races the Unleash fetch)
// can `await featureToggles/whenAuthFlagsReady` instead of evaluating too early.
let _authFlagsReadyResolve = null
let _authFlagsReadyPromise = new Promise((resolve) => { _authFlagsReadyResolve = resolve })
const _resolveAuthFlagsReady = () => {
  if (_authFlagsReadyResolve) {
    _authFlagsReadyResolve(true)
    _authFlagsReadyResolve = null
  }
}
// Re-arm the gate for the next context load (e.g. when the user flag is reset on
// leaving a shared presentation). No-op while a wait is still pending.
const _resetAuthFlagsReady = () => {
  if (!_authFlagsReadyResolve) {
    _authFlagsReadyPromise = new Promise((resolve) => { _authFlagsReadyResolve = resolve })
  }
}

const _updateAuthFlags = (commit, context) => {
  // We have 2 flag,
  // 1. anonymous
  // 2. auth
  if (!context?.isAuth) {
    commit('setAnonymousFlag', true)
  } else {
    commit('setAuthFlag', true)
  }
}

const mutations = {
  setFeatures(state) {
    const features = unleashClient.getAllToggles()
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})
    state.features = { ...featureMap }
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
  startUnleash() {
    if (canUseNewUnleashMechanism) {
      UnleashService.start()
      return
    }
    // Should be removed after the new mechanism is confirmed as stable
    if (isStartedUnleash) return
    isStartedUnleash = true
    unleashClient.start()
  },
  setAnonymousFlag(state, flag) {
    state.hasLoadedAnonymousFlag = flag
  },
  setAuthFlag(state, flag) {
    state.hasLoadedAuthFlag = flag
    // Drive the reusable readiness gate off the same signal callers already use.
    if (flag) {
      _resolveAuthFlagsReady()
    } else {
      _resetAuthFlagsReady()
    }
  },
  setFeatureFlagInitCoalesced(state, payload) {
    state.featureFlagInitCoalesced = payload
  },
  // SYST-74 — see `presentationFlagInfoByShareCode` in the state above.
  setPresentationFlagInfoByShareCode(state, payload) {
    state.presentationFlagInfoByShareCode = payload || null
  },
}

const actions = {
  async initializeFeatureToggles({ commit, dispatch }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      unleashClient.on('initialized', async () => {
        const features = unleashClient.getAllToggles()
        if (!features || !features.length) {
          await unleashClient.updateContext({
            presenterLanguage: store.state.presenterLocale || 'en',
          })
          commit('startUnleash')
        }
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
      unleashClient.on('ready', async () => {
        commit('setFeatures')
        commit('setLoading', { type: 'initialization', loading: false })
        if (canUseCacheProfileOnLoad) {
          const context = unleashClient.getContext()
          _updateAuthFlags(commit, { ...context, isAuth: !!context.email })
        }
        if (unleashClient.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT) {
          dispatch('slideMarketplace/fetchMarketplaceSlideTypes', null, { root: true }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to pre-fetch marketplace slide types:', err)
          })
        }
      })

      // on update context
      // More detail: https://docs.getunleash.io/reference/sdks/javascript-browser#available-events
      unleashClient.on('update', () => {
        commit('setFeatures')
        if (unleashClient.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT) {
          dispatch('slideMarketplace/fetchMarketplaceSlideTypes', null, { root: true }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to pre-fetch marketplace slide types:', err)
          })
        }
        // log context
        // console.log('context :', JSON.stringify(state.context))
        // We have 2 flag,
        // 1. anonymous
        // 2. auth
        // CAUTION: probably race condition (context changes during inflight process)
        // Why removed? Because I realize this piece of code won't be called if the unleash detect nothing change from api
        // For short
        // 200 -> those code below run
        // 304 -> the on.('update') hook won't be triggered and those code below won't be run
        // if (!state?.context?.isAuth) {
        //   commit('setAnonymousFlag', true)
        // } else {
        //   commit('setAuthFlag', true)
        // }
      })
      unleashClient.on('error', (error) => {
        // Skip benign, self-healing network failures to avoid flooding Sentry
        // with non-actionable `Failed to fetch` noise (STPANCRAS-PRESENTER-APP-13N4).
        if (isBenignUnleashError(error)) return
        sentry.captureException(error, {
          tags: {
            source: 'featureToggles.module.js',
          },
        })
      })
      unleashClient.on('recovered', () => {
        // this event is only fired if after an error
        // the unleashClient successfully fetch the feature flags from the server
        // so we need to update the feature flags
        // refer to https://github.com/Unleash/unleash-js-sdk/blob/1746b0aa66c31603a3ecc52f0c7f13adb40a03dc/src/index.ts#L93
        commit('setFeatures')
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })
    commit('setContext', context)
    const contextToUse = { ...state.context }
    // Overlaps the feature-settings round trip with the flag fetch. It is still committed only
    // after the flags below, because App.vue treats "feature settings loaded" as "auth flags loaded".
    if (contextToUse.isAuth) {
      store.dispatch('featureSettings/prefetchFeatureSettings')
    }

    try {
      await unleashClient.updateContext(contextToUse)
    } catch (error) {
      // 'Failed to fetch' TypeErrors are expected when the Unleash client internally
      // aborts an in-flight request because a new updateContext() call was made before
      // the previous one completed (e.g., rapid context change from anonymous to
      // authenticated on the login page). These are not actionable and should not be
      // reported to Sentry
      // sentry.captureException(error)
    } finally {
      _updateAuthFlags(commit, contextToUse)
      commit('setLoading', { type: 'contextUpdate', loading: false })
      if (contextToUse.isAuth) {
        store.dispatch('featureSettings/fetchFeatureSettings', { reusePrefetch: true })
      }
    }

    if (!canUseNewUnleashMechanism) {
      // redundant, already handle in the unleashClient.on('update', () => {
      commit('setFeatures')
    }
  },

  /**
    * Initialize user feature context with all necessary fields for experiments
    * @param {Object} context - Vuex action context
    * @param {Object} params - Parameters containing currentUser and presenterLanguage
    * @returns {Promise} - Promise resolving to context update result
    */
  async initializeUserFeatureContext1({
    dispatch, rootState,
  }, { currentUser, presenterLanguage }) {
    try {
      const { plan } = rootState

      const context = {
        // Base context fields (always included)
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        accountId: currentUser.accountId || null,
        presenterLanguage,
        // Required so _updateAuthFlags fires setAuthFlag(true), which flushes
        // the mixpanel_auth queue introduced in AHA-40717. Without this, V1
        // users were stuck with hasLoadedAuthFlag=false and lost all auth-route
        // Mixpanel events (e.g. report-ai-insights CSAT clicks).
        isAuth: true,
        // Reset presentationCreatorEmail set by share-link flows, otherwise
        // it leaks into flag evaluation on the user's own pages.
        presentationCreatorEmail: null,

        // Context fields for experiment-AHA-30200-monthly-to-yearly
        billingCycle: plan?.billingCycle || null,
        billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
          ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
          : null,
        qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
        registeredAt: normalizeRegisteredAt(currentUser.createdAt),
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('initializeUserFeatureContext', context)
      }

      return dispatch('updateFeatureContext', context)
    } catch (error) {
      // Fallback to basic context if extended context fails
      sentry.captureException(error, {
        extra: {
          action: 'initializeUserFeatureContext',
          userId: currentUser?.id,
        },
      })

      const fallbackContext = {
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        accountId: currentUser.accountId || null,
        presenterLanguage,
        isAuth: true,
        presentationCreatorEmail: null,
        registeredAt: normalizeRegisteredAt(currentUser.createdAt),
      }

      return dispatch('updateFeatureContext', fallbackContext)
    }
  },

  // Use this one to await the user flag update
  async initializeUserFeatureContext({
    dispatch,
  }, { currentUser, presenterLanguage }) {
    if (canUseNewUnleashMechanism) {
      return dispatch('initializeUserFeatureContext2', { currentUser, presenterLanguage })
    }
    return dispatch('initializeUserFeatureContext1', { currentUser, presenterLanguage })
  },
  // My 50 cents: awaiting context update is unstable IMO,
  // I am not sure the flag is ready and update to the state at that time
  // The ideal phase to confirm it to be ready is inside unleashClient.on('update', () => {
  // or the the setFeatures mutation to be more exact
  // We can set 1 more Unleash context value like:
  //  isAuth: true, and check it at the set time
  // But it's already worked and use by our colleagues, so let see if it works
  async initializeUserFeatureContext2({
    dispatch, rootState, commit, state,
  }, { currentUser, presenterLanguage }) {
    async function _doInitializeUserFeatureContext() {
      try {
        const { plan } = rootState

        if (!currentUser) {
          currentUser = rootState.currentUser
        }
        if (!currentUser) {
          currentUser = await UserApi.getUser()
        }

        const context = {
          // Base context fields (always included)
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          accountId: currentUser.accountId || null,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,

          // Context fields for experiment-AHA-30200-monthly-to-yearly
          billingCycle: plan?.billingCycle || null,
          billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
            ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
            : null,
          qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
          registeredAt: normalizeRegisteredAt(currentUser.createdAt),
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('initializeUserFeatureContext', context)
        }

        return dispatch('updateFeatureContext', context)
      } catch (error) {
        // Fallback to basic context if extended context fails
        sentry.captureException(error, {
          extra: {
            action: 'initializeUserFeatureContext',
            userId: currentUser?.id,
          },
        })

        const fallbackContext = {
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          accountId: currentUser.accountId || null,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,
          registeredAt: normalizeRegisteredAt(currentUser.createdAt),
        }

        return dispatch('updateFeatureContext', fallbackContext)
      }
    }

    // This is to avoid multiple feature flag init
    // And we can invalidate it later if we need to
    if (!state.featureFlagInitCoalesced) {
      commit('setAuthFlag', false)
      commit('setFeatureFlagInitCoalesced', coalesce(() => _doInitializeUserFeatureContext()))
    }
    return state.featureFlagInitCoalesced.run()
  },
  async startUnleash({ commit }) {
    commit('startUnleash')
  },

  /**
   * AHA-42358: reusable gate that resolves once the authenticated user's flags
   * have loaded. Use this before any flag-gated logic that runs on app load —
   * reading a flag synchronously during boot races the Unleash fetch and yields
   * the default (off), which silently picks the wrong branch (e.g. the entitlement
   * endpoint). The user-flag load itself is kicked off by getCurrentUserAction;
   * this only waits for it.
   *
   * @returns {Promise<boolean>} true if flags loaded in time, false on timeout
   *   (caller should proceed with the safe flags-off default).
   */
  async whenAuthFlagsReady({ state }, payload) {
    const { timeoutMs = DEFAULT_FLAG_READY_TIMEOUT_MS } = payload || {}
    if (state.hasLoadedAuthFlag) return true
    let timer
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(false), timeoutMs) })
    try {
      return await Promise.race([_authFlagsReadyPromise, timeout])
    } finally {
      clearTimeout(timer)
    }
  },

  // Because sharing view is not auth required but we still need the flag
  async handlePresentationLoadFlagUnleashByShareCode({ commit, dispatch }, shareCode) {
    // SYST-74: clear first so a failed request or an in-SPA move to another share link
    // leaves no answer (-> unbounded endpoint), never the previous deck's metadata.
    commit('setPresentationFlagInfoByShareCode', null)
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfoByShareCode(shareCode)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      // SYST-74: capture the date gates for the share-path endpoint choice, keyed by share
      // code. `resetTimeStamp` is absent from the pre-fix / CDN-cached shape; normalising
      // to null fails the reset gate safely rather than reading a leftover. See PR #5108.
      commit('setPresentationFlagInfoByShareCode', {
        shareCode,
        createdAt: respFlagInfo.body.createdAt ?? null,
        resetTimeStamp: respFlagInfo.body.resetTimeStamp ?? null,
      })
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
      return respFlagInfo.body
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      return error
      // do nothing
    }
  },
  async handlePresentationLoadFlagUnleash({ dispatch }, presentationId) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfo(presentationId)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      // do nothing
    }
  },
}

export const comparePresentationCreatedDateWithFlag = (presentation, flag) => {
  if (!presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
  return new Date(presentation.createdAt) > UnleashFlagTurnOnTime[flag]
}

const _getters = {
  // BOO-203: a URL-param override (?unleashOverride=<flag>:on|off|<variant>) wins
  // over the real Unleash value on non-production envs, so a flag can be forced
  // on/off for local/sandbox/staging testing. Ignored on production and a no-op
  // when the flag isn't overridden (getUnleashOverride returns undefined).
  isFeatureEnabled: (state) => (featureName) => {
    const override = getUnleashOverride(featureName)
    if (override) return override.enabled
    return state.features[featureName] && state.features[featureName].enabled
  },
  // BOO-203: this whole-map getter is handed to the zoid-embedded micro-frontends
  // (onboarding / report / team-management) and feeds the Mixpanel flag context,
  // so it must honor the override too — otherwise a forced flag would render in
  // the host UI while the embeds and analytics still saw the real value. Returns
  // `state.features` untouched when nothing is overridden.
  featureFlags: (state) => () => {
    return applyUnleashOverrides(state.features)
  },
  hasLoadedAnonymousFlag: (state) => state.hasLoadedAnonymousFlag,
  hasLoadedAuthFlag: (state) => state.hasLoadedAuthFlag,
  getVariant: (state) => (featureName) => {
    const override = getUnleashOverride(featureName)
    if (override) return override.variant
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  canUseReditusReferral: (_, getters) => {
    return getters.getVariant(REDITUS_REFERRAL_FLAG)?.name === REDITUS_REFERRAL_TREATMENT_VARIANT
  },
  canShowReferAndEarnCard: (_, getters) => {
    return getters.getVariant(REFER_EARN_HOME_CARD_FLAG)?.name === REFER_EARN_HOME_CARD_TREATMENT_VARIANT
  },
  // US 1 (data-driven slide picker): a multi-variant A/B flag comparing two
  // placements of the server-driven picker. Returns the raw variant name so
  // callers can branch AND forward it as the experiment dimension to analytics:
  //   'data-driven' => the picker in the left popup slot (like the default picker)
  //   'canvas'      => the picker overlaid on the slide (#main-slide)
  //   'control' | 'disabled' => the existing picker is untouched
  dataDrivenSlidePickerVariant: (_, getters) => {
    return getters.getVariant(DATA_DRIVEN_SLIDE_PICKER_FLAG)?.name || 'disabled'
  },
  isDataDrivenPickerLeftPopup: (_, getters) => {
    return getters.dataDrivenSlidePickerVariant === 'data-driven'
  },
  isDataDrivenPickerCanvas: (_, getters) => {
    return getters.dataDrivenSlidePickerVariant === 'canvas'
  },
  // True for any data-driven placement: every arm IS the data-driven picker,
  // just in a different spot — only `control` keeps the original picker (and
  // `disabled` is the flag-off sentinel). Expressed as "not control / not
  // disabled" rather than enumerating placements, so a NEW placement variant is
  // covered automatically. The picker components still self-gate per placement
  // via isDataDrivenPickerLeftPopup / isDataDrivenPickerCanvas; this is a roll-up
  // (used by the marketplace-fetch gate and the read-only store stub).
  canUseDataDrivenSlidePicker: (_, getters) => {
    const variant = getters.dataDrivenSlidePickerVariant
    return variant !== 'control' && variant !== 'disabled'
  },
  // AHA-44472: route the editor's presentation-detail fetch to the v3 backend
  // endpoint (drop-in, contract-identical replacement for v2 — same body, same
  // ignore-too-much-slide-options behaviour) instead of v2. Off => v2 (current
  // behaviour). See api/presentation.js getPresentationDetails().
  canUsePresentationDetailV3: (_, getters) => {
    return getters.isFeatureEnabled('AHA-44472-prez-detail-v3')
  },
  // Keep-alive preload for aha-slide-plugin canvases: while presenting, boot the
  // next plugin slide's iframe ahead of time (active:false) and reveal it on
  // navigation without recreating the iframe. Off => current close+recreate path.
  canUsePreloadAhaSlidePlugin: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46422-preload-aha-slide-plugin')
  },
  // AHA-46885: the next/back slide entrance transition (view-transition cross-fade,
  // with a plain fade fallback) layered ON TOP of the keep-alive preload above. It
  // only counts as on when `canUsePreloadAhaSlidePlugin` is also on — see the
  // `hasSlideEntranceFade` getter, which ANDs the two. Off => preload keeps working
  // exactly as before; slides just use their original entrance animation instead of
  // the cross-fade. This is the independent kill switch for the transition alone.
  canUseNextBackSlideTransition: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46885-next-back-slide-transition')
  },
  // AHA-46714: ROLLBACK switch, inverted on purpose. The fix (editor Back
  // exits to the last recorded meta.isDestination route) ships ON for
  // everyone with the flag OFF, which is its production default. Turning the
  // flag ON restores the legacy previousRoute / document.referrer /
  // history.length logic, preserved verbatim in backV4Action. That is the
  // one-switch undo if the fix misbehaves, with no deploy needed.
  canUseLegacyEditorBack: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46714-editor-back-legacy-rollback')
  },
  // Single source of truth for the AHA-32529 multi-select apply fix. This is a
  // release switch rather than an experiment: enabled opts in, while disabled
  // and the not-yet-loaded state keep the pre-fix path.
  isMultiSelectApplyFixEnabled: (_, getters) => {
    return Boolean(getters.isFeatureEnabled('release-AHA-32529-editor-multi-select-apply-fix'))
  },
  // BOO-293: the search filter + pinned block + listbox/combobox rebuild of the
  // presenter language switcher dropdown. Off => the plain click-to-select
  // dropdown (pre-BOO-293 behaviour), with no search box and no rebuild. Safe
  // rollout kill switch for the whole feature.
  canUseLanguageSwitcherSearch: (_, getters) => {
    return getters.isFeatureEnabled('BOO-293-language-switcher-search')
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  // AHA-46571 / BOO-630: gates the marketplace lifetime-code redemption UI (the
  // /redeem page and the "Have a marketplace code?" section on My Plan). FE-only
  // gate — the backend redeem endpoint is always available.
  canUseLifetimeOnMarketplace: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46571-lifetime-on-marketplace')
  },
  canUseLeaderboardAudienceRefreshFix: (_, getters) => {
    return getters.isFeatureEnabled('PROD-2-leaderboard-audience-refresh')
  },
  // Gates the redesigned theme grid and editor: the participant-view preview
  // column beside the presenter preview in the Create/Edit Theme modal (plus its
  // wider modal), and the richer custom-theme thumbnails (image normalisation)
  // with drag-to-reorder in the My Themes grid. Off => the pre-existing single
  // presenter-view preview, plain thumbnails, and a static grid.
  canUseThemeManagementV2: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47841-theme-management-v2')
  },
  canUseNewEnterpriseInquiriesForm: (_, getters) => {
    return getters.isFeatureEnabled('release-enterprise-inquiries-form')
  },
  // AHA-43391: hide the Team management item in the left sidebar for non
  // owner/admin members (enabled for ATB first).
  canUseHideTeamManagementForNonAdmin: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-43391-hide-team-management-non-admin')
  },
  // AHA-46323: a teamless user must be able to create a team from the sidebar
  // modal with a team name alone. Flag ON turns that modal into a pure
  // create-team form (no invitees) posting to POST /api/accounts; flag OFF keeps
  // today's "team name + at least one invitee -> POST /api/invitations" flow.
  canUseSidebarCreateTeam: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46323-create-team')
  },
  canUseNetworkIndicatorV2: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40903-update-network-indicator')
  },
  // AHA-43163: default the Play-as-team "Auto-assign teams" toggle to ON when unset.
  canAutoAssignTeamsDefaultOn: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43163-auto-assign-teams-default')
  },
  canUseCreateFromTemplateDeepLink: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-42424-create-from-template-deeplink')
  },
  // AHA-47744: "Create Content V2 slide" button on a freestyle slide's Content
  // tab, which converts the slide through the ahaaiv2 converter into a new
  // content-v2 slide. Variant-based: only an explicit `treatment` opts a user
  // in; control / disabled / not-yet-loaded hides the button and changes
  // nothing else.
  canConvertContentV1ToV2: (_, getters) => {
    return getters.getVariant(CONVERT_CONTENT_V1_TO_V2_FLAG)?.name === 'treatment'
  },
  canUseOnboardingIkeaChecklist: (_, getters) => {
    const variant = getters.getVariant('AHA-42696-onboarding-ikea-checklist-presenter')
    return variant?.name === 'onboarding-ikea-checklist-presenter'
  },
  // BOO-58 / AHA-43262: resolves the active arm of the direct-feature-upgrade
  // experiment. The Unleash strategy is constrained to free users (plan=free),
  // so this only returns a treatment arm for free users; everyone else (and a
  // flag that is off) falls through to `disabled`. Returns the raw variant name
  // ('monthly-upgrade' | 'yearly-upgrade' | 'disabled') so callers can both
  // branch on it AND forward it as the experiment dimension to analytics.
  directFeatureUpgradeVariant: (_, getters) => {
    return getters.getVariant(DIRECT_FEATURE_UPGRADE_FLAG)?.name || 'disabled'
  },
  canUseCorrectCropForImageReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-correct-crop-for-image-replace')
  },
  // AHA-41738: gate the Design panel Themes/Customise sub-tab URL sync to use
  // router.replace (no history entry) instead of router.push. Off = today's
  // production behaviour unchanged (router.push).
  canUseEditorDesignTabHistoryReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-editor-design-tab-history-replace')
  },
  canUseNewAutocomplete: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28900-autocomplete')
  },
  canUseGoogleDriveV2: (_, getters) => {
    return getters.isFeatureEnabled('release-google-drive-v2')
  },
  // Direct feature-upgrade modal: replaces the indigo upgrade popover + immediate
  // Stripe redirect with an in-app modal that shows the feature explanation and
  // lets the user pick a billing cycle (monthly/yearly) before checking out.
  // Variant-gated (not a plain on/off) so it can run as one arm of the upgrade
  // experiment; the new modal shows only for the `feature-upgrade-new-ui` variant.
  canUseFeatureUpgradeModal: (_, getters) => {
    return getters.getVariant('release-feature-upgrade-modal')?.name === 'feature-upgrade-new-ui'
  },
  canUseRequestAccessPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28682-request-access-presentation')
  },
  canUseGenerateOptions: (_, getters, __, rootGetters) => {
    const { currentSlideType } = rootGetters
    if (currentSlideType === 'categorise') {
      return getters.isFeatureEnabled('release-generate-answers-categorise')
    }
    return GENERATE_OPTIONS_ROLLED_OUT_SLIDE_TYPES.includes(currentSlideType)
  },

  canUseQuestionSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-question-suggestions')
  },
  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation) return false
      return comparePresentationCreatedDateWithFlag(rootState.presentation, flag)
    }
  },

  canUseGoogleSlideAddonSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29230-google-slide-add-on-sync')
  },
  canUseGoogleSlidesSinglePrez: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39459-google-slides-single-prez')
  },
  canUsePptFileSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-ppt-file-sync')
  },
  canUseGoogleDriveSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-google-drive-sync')
  },
  canUseOneDriveSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-onedrive-sync')
  },
  canUseNewComunityTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-organising-community-templates')
  },
  canDisplayPricingDuringOnboarding: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41565-onboarding-pricing-step')
    return variant?.name === 'treatment'
  },
  canUseShareableTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-shareable-template')
  },
  canUseReleaseNewDiscoverTemplates: (_, getters) => {
    return getters.isFeatureEnabled('release-new-discover-templates')
  },
  canUseExperimentCSATIntoTemplate: (_, getters) => {
    return getters.isFeatureEnabled('experiment-csat-into-template')
  },
  canUseThirdPartyIntegrationsInShareModal: (_, getters) => {
    return getters.isFeatureEnabled('release-promotion-3party-33784')
  },
  canUseExportRedshift: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift')
  },
  canUseExportRedshiftV4: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v4')
  },
  canUseExportRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v5')
  },
  fullReleaseRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('full-release-export-redshift-v5')
  },
  // AHA-47321 ("Move excel report to PG"): when ON, the v5 Excel export reads from
  // the Postgres data warehouse (…/excel/pgv5) instead of Redshift. Default OFF —
  // OFF keeps the existing Redshift v5 endpoints unchanged.
  canUseExcelPgV5Export: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47321-excel-pg')
  },
  // AHA-47552: when ON, a rootPresentationId from the report app routes the export to the
  // multi-session endpoint. sessionOrder alone cannot decide this — it is null both for the
  // root deck and for a collaborator's cloned master presented but never reset, so the clone
  // was sent to the deck-level endpoint, which 403s for the deck owner. Default OFF — OFF
  // keeps the existing sessionOrder behaviour exactly. The flag only ever ADDS a route to the
  // multi-session endpoint: a missing rootPresentationId (report app not yet deployed) falls
  // through to the sessionOrder rule, so enabling this before the report app ships is inert
  // rather than harmful.
  canUseExportRouteByRootPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47552-export-route-by-root-prez')
  },
  // AHA-46264: route plain deck PDF export through the Cloudflare Worker (proxied by our API).
  canUseCloudflarePdfExport: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46264-cloudflare-worker-pdf')
  },
  canRemovePowerPointButton: (_, getters) => {
    return getters.isFeatureEnabled('release-remove-powerpoint-30061')
  },
  // AHA-43226 + AHA-46751: auto-start needs the flag AND a host that can READ SLIDE TAGS.
  // Entering Slide Show wakes every add-in frame in the deck at once — the active view and the
  // selected slide are DOCUMENT-global signals, so each frame sees the same shown slide. The tag
  // on that slide is the only thing that tells the frames apart (services/office/current-slide),
  // so without tag support every frame would conclude the shown slide is its own and start,
  // leaving the audience oscillating between them. A host with no tags (iPad, volume-licensed
  // perpetual Office incl. 2024 LTSC) instead keeps the manual "click to start" modal, which
  // SlideWrapper shows precisely when this getter is false.
  //
  // `rootState.addinPPTPlatform` is read for its REACTIVITY as much as its value: Vuex getters
  // are cached computeds, and canReadSlideTags() probes window.PowerPoint, which is not reactive.
  // The platform is committed by captureOfficePlatform() as Office initialises — the same moment
  // the probe starts answering — so touching it makes this getter re-evaluate then, instead of
  // freezing on a `false` computed before office.js finished loading. Outside the add-in it is
  // null, which is the right answer anyway.
  canUseAddinPowerpointAutoStart: (_, getters, rootState) => {
    if (!getters.isFeatureEnabled('AHA-43226-ppt-auto-start')) return false
    return Boolean(rootState.addinPPTPlatform) && canReadSlideTags()
  },
  // AHA-43274: end the running quiz when the PPT slide show moves onto a slide that carries no
  // AhaSlides add-in (see services/office/current-slide's SETTLED-UNKNOWN verdict, wired through
  // services/office/index.js). The auto-STOP counterpart to canUseAddinPowerpointAutoStart above:
  // flag off must be a pure no-op, so the ppt-addin/stopActiveSlidePPT action is gated on this and
  // returns before touching any state or API when it is false — old behaviour (the quiz keeps
  // running on a plain slide) is preserved verbatim.
  canUsePptAutoStopQuiz: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43274-ppt-auto-stop-quiz')
  },
  // Silences a PowerPoint add-in frame whose slide is not the one selected in the EDITOR — the
  // edit-view counterpart to canUsePptAutoStopQuiz's stand-down, covering the same three channels it
  // does: slide-title audio, the BGM element in MusicToggle, and quiz sound effects. Also carries
  // the mount-time autoplay recovery in ensureMusicAutoplayAction, so switching this off restores
  // the frame's audio behaviour whole rather than in pieces.
  //
  // Flagged because the guard behind it FAILS CLOSED: the frame goes quiet whenever the verdict is
  // not a positive "mine", which includes "nobody could tell". The verdict comes from
  // DocumentSelectionChanged, which is confirmed on PowerPoint desktop and unverified on the web,
  // where the sibling ActiveViewChanged never fires at all. If that event turns out not to be
  // serviced on some host, slide audio there stops working in the editor with no way to recover it
  // short of a deploy — so the kill switch is the point, not ceremony.
  canUsePptEditSlideAudioGate: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47085-ppt-edit-slide-audio-gate')
  },
  canUsePptDefaultPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46192-ppt-default-presentation')
  },
  // AHA-43111: unblock the add-in on PowerPoint for the web. When off, the
  // PowerpointOnlineNotSupport modal keeps blocking web exactly as before.
  canUsePptOnline: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43111-ppt-online')
  },
  // AHA-47084: gate handing the arrow keys back to the host PowerPoint deck. The add-in frame
  // owns the keyboard while focused, so PowerPoint never sees the key and the deck sits still.
  // Off => the key stays swallowed and the host never moves, exactly as before. Worth a kill
  // switch because this is the one path that drives the presenter's real deck mid-presentation.
  canUsePptKeyboardHandover: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47084-ppt-keyboard-handover')
  },
  // AHA-47957 — repair a click whose release was delivered in the wrong coordinate space, which is
  // what makes every control in the add-in unclickable from PowerPoint's Presenter View pane on an
  // affected multi-monitor setup. Off => the relocated click propagates untouched and the add-in
  // behaves exactly as it does today. Worth a kill switch because it runs on the click path of the
  // entire add-in surface during a live presentation; read per event so switching it off takes
  // effect mid-show without a reload.
  canUsePptPointerGestureRepair: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47957-ppt-pointer-gesture-repair')
  },
  // AHA-47221 — the add-in's new list UI: the dashboard's My Presentations page trimmed to what
  // a narrow task pane can serve. It drops the Import and Share-folder actions, the header's
  // referral gift, and the explicit "Select presentation" column (redundant once the row itself
  // picks — see MainTable, which ANDs the row-click gate on top before removing it), and it adds
  // a Refresh to the list toolbox and to the pane editor's header.
  // Off => the list renders exactly as it did before, every action and column included.
  //
  // Routing is NOT part of this gate. An add-in frame with nothing bound has always been sent to
  // ROUTE_NAMES.PRESENTATIONS and still is on every flag state — pptAddinListRouteName() takes no
  // store and has no branch. The flag decides only what that page then looks like.
  // The PowerPoint build target is part of this gate rather than a separate check at each
  // call site: every consumer wants "the new add-in UI is live in THIS frame", and the two
  // written out side by side was the same conjunction repeated, one v-if away from being got
  // wrong. `isEnableAddinPowerpoint` is build-time (VUE_APP_ENABLE_ADD_IN_POWERPOINT), so in
  // the ordinary web build this getter is false whatever Unleash says.
  // AHA-46329 is part of the gate for the same reason: the new UI is built ON the multi-select
  // flow — its editor entrance is where tick boxes and the add-slides bar live — so shipping it
  // without AHA-46329 would offer a selection UI whose destination flow is switched off. AHA-46329
  // off => the add-in's list keeps its full pre-47221 chrome, exactly as before.
  //
  // AHA-47221's own flag has gone from the gate now that it is at 100%: a rollout term that is
  // true everywhere only leaves a second switch that can turn the UI off, and the UI is no longer
  // the thing being rolled out. AHA-46329 remains the switch for this whole flow.
  //
  // In its place the two HOST CAPABILITIES the flow actually needs, which the rollout flag was
  // quietly standing in for (they gate the selector the same way — see ppt-slide-selector-gate):
  //   - isBulkSlideAddSupported(): exportAsBase64 (PowerPointApi 1.8), the only way to put a new
  //     add-in-bearing slide in the deck. Absent on iPad and on volume-licensed perpetual Office
  //     (incl. 2024 LTSC), where the add-slides bar would offer a flow that cannot complete.
  //   - canReadSlideTags(): the per-slide tags that tell sibling frames apart. Without them the
  //     editor entrance cannot know which frame it is, which is what the tick boxes act on.
  //
  // `rootState.addinPPTPlatform` is read for its REACTIVITY, exactly as canUseAddinPowerpointAutoStart
  // above reads it: Vuex getters are cached computeds and both probes read window.PowerPoint, which
  // is not reactive. The platform is committed by captureOfficePlatform() as Office initialises —
  // the same moment the probes start answering — so touching it makes this getter re-evaluate then
  // instead of freezing on the `false` it computed before office.js finished loading. With the
  // rollout flag gone that is no longer optional: AHA-46329 was the last reactive term that could
  // have invalidated the cache on its own.
  canUsePptNewUi: (_, getters, rootState) => {
    if (!rootState.isEnableAddinPowerpoint) return false
    if (!rootState.addinPPTPlatform) return false
    // A dialog window has no Rich API and no window.PowerPoint, so both host-capability probes
    // below always answer false there and would strand the dialog editor on the old UI. The
    // dialog never inserts slides or reads tags itself (it hands those to the pane), so it needs
    // neither capability — gate it on the rollout flag alone.
    if (isPptDialog()) return getters.canUsePptMultipleSelect
    return getters.canUsePptMultipleSelect && isBulkSlideAddSupported() && canReadSlideTags()
  },
  // AHA-46329: gate the PPT add-in multi-slide selector. Off => clicking a presentation
  // in the add-in's list opens the editor directly, exactly as before.
  canUsePptMultipleSelect: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46329-ppt-multiple-select')
  },
  // AHA-46754: gate the ROW-BODY entry point into that selector — clicking a presentation
  // anywhere other than its "Select presentation" button. Needs BOTH flags: there is no
  // row-click without the selector it opens. Off => only the button opens the selector and
  // the rest of the row is inert again (thumbnail/name cells reopen the editor), exactly as
  // before AHA-46329. The button itself stays on canUsePptMultipleSelect alone.
  canUsePptPrezListRowClick: (_, getters) => {
    return getters.canUsePptMultipleSelect && getters.isFeatureEnabled('AHA-46754-ppt-prezlist-click')
  },
  // AHA-46375: gate the "resize the add-in frame to fill the slide" pass. The flag's
  // variant payload is a JSON array of the platforms the resize may run on, e.g.
  // ["PC", "mac"], so a platform that misbehaves can be dropped without a deploy. No
  // usable list (no variant, empty list, unparseable JSON) means every platform — a
  // misconfigured variant must not silently switch the feature off everywhere.
  // `addinPPTPlatform` is committed from Office.context.platform during add-in load,
  // before the resize runs; outside the add-in it is empty, so a variant list matches
  // nothing and the resize (which needs a PowerPoint host anyway) stays off.
  canUsePptAutoResize: (_, getters, rootState) => {
    if (!getters.isFeatureEnabled(PPT_AUTO_RESIZE_FLAG)) return false
    const platforms = parsePptAutoResizePlatforms(getters.getVariant(PPT_AUTO_RESIZE_FLAG))
    if (!platforms) return true
    return platforms.some((token) => {
      return PPT_VARIANT_TOKEN_TO_PLATFORM[String(token).trim().toLowerCase()] === rootState.addinPPTPlatform
    })
  },
  canUseGoogleSlidesPromote: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33785')
  },
  canUseIntegrationsSection: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33786')
  },
  canUsePptVisualCue: (_, getters) => {
    return getters.isFeatureEnabled('DSN-814-ppt-visual-cue')
  },
  canUseMovePresentations: (_, getters) => {
    return getters.isFeatureEnabled('move-presentation')
  },
  canUseEveryoneHasAnswered: (_, __, ___, rootGetters) => {
    return rootGetters['liveStatsV2/canUseLiveStatsForQuizV2']
  },
  canUseDisableClientCount: (_, getters) => {
    return getters.isFeatureEnabled('AHA-45913-disable-client-count')
  },
  canUseTransactionalSlideCacheUpdate: (_, getters) => {
    return getters.isFeatureEnabled('transactional-slide-cache-update')
  },
  isEnabledNewIntegrations: (_, getters) => {
    return getters.isFeatureEnabled('is-enabled-new-integrations')
  },
  canUseNewPricing: (_, getters) => {
    return getters.isFeatureEnabled('release-new-pricing-presenter')
  },
  canUseSCIM: (_, getters) => {
    return getters.isFeatureEnabled('release-scim')
  },
  canUseScimManualManagement: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42627-scim-manual-management')
  },
  canResetPresentationTrackingContext: (_, getters) => {
    return getters.isFeatureEnabled('release-reset-presentation-tracking-context')
  },
  canUserOfferingMonthlyToYearly: (_, getters) => {
    // Allow the banner when the wording-v2 experiment has a valid arm...
    const wordingVariant = getters.getVariant('AHA-40735-abtest-wording-upgrade-v2')
    if (wordingVariant?.name === 'control' || wordingVariant?.name === 'exclusive-40-off') {
      return true
    }
    // ...OR when the BOO-11 discount-coupon experiment is active (any `discount-*` variant).
    // Without this check, enabling only AHA-40735-abtest-discount-coupon returns false here
    // and the banner is gated off before it can ever render.
    const discountVariant = getters.getVariant(MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG)
    return !!(discountVariant?.name?.startsWith(DISCOUNT_VARIANT_NAME_PREFIX))
  },
  // BOO-11: resolves the active monthly→yearly discount arm.
  // Returns { name, discountPercentage } for a discount arm, or null for control / flag-off.
  // discountPercentage is read from the Unleash variant payload when configured, with a
  // fallback to parsing it from the variant name (e.g. `discount-20pct` → 20). This means
  // it works whether or not the Unleash variant has a payload value set — percentages can
  // be managed entirely from the Unleash UI without a frontend deploy.
  monthlyToYearlyDiscountVariant: (_, getters) => {
    const variant = getters.getVariant(MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG)
    if (!variant?.name?.startsWith(DISCOUNT_VARIANT_NAME_PREFIX)) return null

    // Prefer payload value (configured on Unleash), fall back to parsing from variant name.
    const payloadValue = variant.payload?.value
    const fromPayload = payloadValue != null ? Number(payloadValue) : NaN
    const fromName = parseInt(variant.name.replace(/^discount-(\d+)pct$/, '$1'), 10)
    const discountPercentage = Number.isFinite(fromPayload) ? fromPayload : fromName

    if (!Number.isFinite(discountPercentage) || discountPercentage <= 0) return null
    return { name: variant.name, discountPercentage }
  },
  // Monthly-cancel retention experiment treatment: show the yearly retention offer
  // (deep-discounted monthly→yearly switch) instead of the legacy 1-free-month offer.
  // Only the `yearly-offer` treatment arm enables it — control / other arms / flag off
  // all keep the legacy flow, byte-for-byte unchanged.
  canUseMonthlyCancelYearlyOffer: (_, getters) => {
    return getters.getVariant(MONTHLY_CANCEL_YEARLY_OFFER_FLAG)?.name === CANCELLATION_OFFER_TREATMENT_VARIANT
  },
  canUseNewResizeImageApi: (_, getters) => {
    return getters.isFeatureEnabled('AHA-37355-use-lambda-resize')
  },
  canUseReportSession: (_, getters) => {
    return getters.isFeatureEnabled('release-report-session')
  },
  // AHA-47349-presenter-can-create-new-slide-type
  // Requires the new slide marketplace too: developer slide types are appended to
  // and rendered from the marketplace catalogue, so without it the Developer menu
  // would appear but the types could never load. Gating both here keeps the whole
  // feature (menu, route, page, append, render) behind a single prerequisite.
  canUseCreateNewSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-47349-presenter-can-create-new-slide-type')
      && getters.canUseNewSlideMarketPlace
  },
  canUseDirectBulkPurchaseLinks: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38492-direct-bulk-purchase-links')
  },
  canUsePresentationSearchWithShared: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38736-presentation-search-with-shared')
  },
  canHideAmount: (_, getters) => {
    return getters.isFeatureEnabled('release-my-plan-hide-amount')
  },
  canShowTeamFreeBadge: (_, getters) => {
    return getters.isFeatureEnabled('release-show-team-free-badge')
  },
  canUseButtonShareFolder: (_, getters) => {
    return getters.isFeatureEnabled('release-button-share-folder')
  },
  canUseISOStringForEventStartedAt: (_, getters) => {
    return getters.isFeatureEnabled('release-iso-string-for-event-started-at')
  },
  // https://ahaslides.atlassian.net/browse/AHA-39583
  // We change the current behavior, so I put this flag case we wanna revert
  // Current behavior: can't move a presentation to my presentation list
  // New behavior: can move a presentation to my presentation list
  canMovePresentationToMyPresentationList: (_, getters) => {
    return getters.isFeatureEnabled('release-allow-move-to-my-presentation')
  },
  canHideTeamSection: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-sidebar-team-section')
  },
  canUseSlidesReport: (_, getters) => {
    return getters.isFeatureEnabled('release-report-question')
  },
  canGetNewAccountMembers: (_, getters) => {
    return getters.isFeatureEnabled('release-new-api-get-account-member')
  },
  canUseAccountManager: (_, getters) => {
    return getters.isFeatureEnabled('release-account-manager')
  },
  canReplyToSender: (_, getters) => {
    return getters.isFeatureEnabled('release-reply-to-sender')
  },
  canUseParticipantProfiles: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40041-participant-profiles-full-list')
  },
  canUseReportAsIframe: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40370-report-as-iframe')
  },
  canUseParticipantProfileResponses: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40332-participant-profile-responses')
  },
  canUseNewSfxSettings: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39704-presenting-sfx')
  },
  canUseMusicAndSoundSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43591-music-and-sound-settings')
  },
  // TODO(cleanup): after 2026-09-02, remove this getter and every callsite —
  // AudioMigrationNotice.vue, its <AudioMigrationNotice> mounts inside
  // AudioEditPanel.vue, and the un-hide logic in RightNav.vue + RightNav.v2.vue.
  // Post-cutoff the Audio tab should stay hidden whenever the Music & sound
  // flag is on (the pre-migration-notice staging behaviour). See PR #5183.
  canShowAudioMigrationNotice: (_, getters) => {
    if (!getters.canUseMusicAndSoundSettings) return false
    const AUDIO_MIGRATION_NOTICE_END = new Date('2026-09-02T00:00:00Z').getTime()
    return Date.now() < AUDIO_MIGRATION_NOTICE_END
  },
  canUseIdeaBoardSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39912-idea-board-slide-type')
  },
  canUsePinOnImageSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40699-pin-on-image-slide-type')
  },
  // AHAM-638: allow a marketplace slide-type iframe to write the host slide
  // title (slide.title) via the updateSlide xprop. Default OFF — when the flag
  // is absent isFeatureEnabled returns falsy, so no title write is ever accepted
  // and production behaviour is unchanged until this is explicitly turned on.
  canWriteSlideTitleFromMarketplace: (_, getters) => {
    return getters.isFeatureEnabled(MARKETPLACE_SLIDE_TITLE_WRITE_FLAG)
  },
  canUseRankingSlideType: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40791-ranking-slide-type')
  },
  canUseDiagramSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42445-diagram-slide')
  },
  canUseSurveySlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42639-survey-slide')
  },
  canUseSurveyInMoreTypes: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46167-survey-in-more-types')
  },
  canUseContentV2SlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42560-content-v2-slide')
  },
  canShowNoResultPopup: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40816-show-no-response-popup')
  },
  canFixContextMenuPosition: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29934-fix-context-menu')
  },
  canUseSessionReport: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40593-session-report')
  },
  canUsePresentationEditLink: (_, getters) => {
    return getters.isFeatureEnabled('AHA-32274-presentation-edit-link')
  },
  canUseAIConsentSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40635-release-ai-consent-settings')
  },
  canUseNewBackstage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41193-new-ui-backstage')
  },
  canUseNewUiBackStage: (_, getters, __, rootGetters) => {
    return rootGetters.isBackstage && getters.isFeatureEnabled('AHA-41193-new-ui-backstage')
  },
  canUseAIConsentTeamTab: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40635-ai-consent-team-tab')
  },
  canUseNewTeamManagement: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39002-team-management')
  },
  canUseEmbedTeamApp: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41755-embed-team-app')
  },
  canUseAIConsentTeamSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40914-release-ai-consent-team-settings')
  },
  canUsePresentationDefaults: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41118-presentation-defaults')
  },
  canUseSamePricingDisplay: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41159-same-pricing-display')
    return variant?.name === 'treatment'
  },
  canUseTeamSettingsPaywall: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41396-team-settings-paywall')
  },
  canUseWebflowIntegrationPage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41735-use-webflow-integration-page')
  },
  canUseCopyCastingUrl: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41709-copy-casting-url')
  },
  canUseInlineCsatForAllSlideTypes: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41701-inline-csat-for-all-slide-types')
  },
  canUseCreateWithAIHomepage: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41524-create-with-ai-homepage')
    return variant?.name === 'treatment'
  },
  canShowCreateWithAIOnHomepage: (_, getters, rootState, rootGetters) => {
    const flagEnabled = getters.canUseCreateWithAIHomepage
    const aiDisabled = rootGetters['featureSettings/isFeatureDisabled']('aiContentGeneration')
    const isAuthenticated = !!rootState.currentUser?.id
    return flagEnabled && !aiDisabled && isAuthenticated
  },
  canUseNewHomepage: (_, getters, __, rootGetters) => {
    // The new home is an aha-workspace embed that only mounts with a usable baseUrl —
    // a manifest without one makes the embed render blank with no HomePage2025 fallback,
    // so gate on baseUrl (as iframeUrl and editor.module already assume). Lets AHA-47667
    // ramp independently of the embed flag (AHA-42142) until the manifest is mountable.
    const workspaceApp = rootGetters['embedApp/manifestById'](WORKSPACE_APP_ID)
    return Boolean(workspaceApp?.baseUrl) && getters.isFeatureEnabled('AHA-47667-new-homepage')
  },
  canUseInlineCSATForBackstage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41701-inline-csat-for-backstage')
  },
  canUsePresentingControlAndStatusPanels: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41850-presenting-control-and-status-panels')
  },
  // Host-rendered slide-plugin action buttons. Requires the new control bar
  // (the buttons teleport into its ncb-center) plus its own rollout flag.
  canUsePluginActionButtons: (_, getters) => {
    return getters.canUsePresentingControlAndStatusPanels
      && getters.isFeatureEnabled('AHA-43142-plugin-buttons')
  },
  canUseFakeDoorUploadLimit: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-41824-release-fake-door-upload-limit')
      && rootState.currentUser?.plan === 'free'
  },
  canUseUpdateEditorUi: (_, getters) => {
    const variant = getters.getVariant('AHA-42401-update-editor-ui')
    return variant?.name === 'new-ui'
  },
  canUseUpdateSettingModal: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42401-update-setting-modal')
  },
  canUseTemplateLibraryAnywhere: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-42423-template-library-anywhere')
  },
  canUseAdminAnalyticsStreamlit: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42209-admin-analytics-streamlit')
  },
  canUseShowHideControlPresenting: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41564-show-hide-control-presenting')
  },
  canSeparateVolumeControl: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42126-separate-the-volume-control')
  },
  canUseSidebarRedesign: (_, getters, rootState) => {
    // Sidebar redesign must never apply to the Google Slides integration.
    if (rootState.integrationPlatform === 'GoogleSlide') return false
    return getters.isFeatureEnabled('release-AHA-42765-sidebar-redesign')
  },
  // Two-level "My Files" menu (All / Presentations / Surveys) that replaces the
  // Presentations branch. It only renders inside the redesigned sidebar, and the
  // caller must also check that the aha-workspace embed app is registered —
  // see embedApp/hasWorkspaceApp.
  //
  // Gated on the variant NAME, not on enabled/disabled: a boolean check cannot tell
  // "control" apart from "the flag never loaded", and conflating the two corrupts the
  // Mixpanel exposure split. Anything that isn't an explicit treatment is control.
  canUseMyFilesMenu: (_, getters) => {
    const variant = getters.getVariant(MY_FILES_MENU_FLAG)
    return variant?.name === MY_FILES_MENU_VARIANTS.TREATMENT
  },
  // AHA-46654 — sidebar Trash targets the workspace app's merged trash
  // (/trash: presentations + ahafiles files, surveys included) instead of the
  // legacy presentations-only /apps/trash-bin. Only meaningful alongside
  // canUseMyFilesMenu (the retargeted entry lives in that sidebar branch).
  // Same variant-name gate as canUseMyFilesMenu, for the same reason.
  canUseWorkspaceTrashSidebar: (_, getters) => {
    const variant = getters.getVariant(WORKSPACE_TRASH_FLAG)
    return variant?.name === WORKSPACE_TRASH_VARIANTS.TREATMENT
  },
  // AHA-46648 — sidebar Shared-with-Me targets the workspace app's merged
  // shared listing (/shared: folders + decks + ahafiles files, surveys
  // included) instead of the legacy presentations-only
  // /apps/shared-presentations. Only meaningful alongside canUseMyFilesMenu
  // (the retargeted entry lives in that sidebar branch). Same variant-name
  // gate as canUseMyFilesMenu, for the same reason.
  canUseWorkspaceSharedSidebar: (_, getters) => {
    const variant = getters.getVariant(WORKSPACE_SHARED_FLAG)
    return variant?.name === WORKSPACE_SHARED_VARIANTS.TREATMENT
  },
  // AHA-47249 — My Files sub-entries for the addon apps, one flag each so the
  // three ramp independently. Lesson and Knowledge base reuse the SAME flags
  // workspace-app gates its Create-new dropdown entries on (see
  // myFilesAddonMenus.constant), so sidebar entry and Create entry appear
  // together. Only meaningful alongside canUseMyFilesMenu (the entries live in
  // that sidebar branch). Same variant-name gate as canUseMyFilesMenu, for the
  // same reason.
  canUseLessonMenu: (_, getters) => {
    const variant = getters.getVariant(LESSON_MENU_FLAG)
    return variant?.name === ADDON_MENU_VARIANTS.TREATMENT
  },
  canUseKnowledgeBaseMenu: (_, getters) => {
    const variant = getters.getVariant(KNOWLEDGE_BASE_MENU_FLAG)
    return variant?.name === ADDON_MENU_VARIANTS.TREATMENT
  },
  canUseCourseMenu: (_, getters) => {
    const variant = getters.getVariant(COURSE_MENU_FLAG)
    return variant?.name === ADDON_MENU_VARIANTS.TREATMENT
  },
  canUseNewSlideMarketPlace: (_, getters) => {
    const variant = getters.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)
    return variant?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT
  },
  // Preview gate for SlideTypeListV5 (new add-slide popup layout). OFF => the
  // add-slide popup renders SlideTypeListV4 exactly as today. Force on locally
  // with `?unleashOverride=AHA-slide-picker-v5:on`.
  canUseSlidePickerV5: (_, getters) => {
    return getters.isFeatureEnabled(SLIDE_PICKER_V5_FLAG)
  },
  // DSN-903: fake-door "Build your own slide type" entry inside the new-slide
  // marketplace. Off => marketplace renders exactly as before (no hero card,
  // no empty-state CTA, no 3-step prompt/reveal/thank-you flow).
  canUseSlidePlatformFakeDoor: (_, getters) => {
    return getters.isFeatureEnabled('DSN-903-slide-platform-fakedoor')
  },
  canUseUpgradeInPreview: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42488-soft-limit')
  },
  canUseEditorNoteSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41303-editor-note-sync')
  },
  // AHA-42054 — whether the Essential plan is hidden from the free-user upgrade
  // funnel. Single source of truth is the shared pricing lineup
  // (pricingLineup/isEssentialExcluded): the presenter reuses the SAME variant the
  // pricing page resolved server-side (request-geo + shared session), so the two
  // surfaces can never disagree about a given viewer. The raw getter (no plan gate)
  // is for callers that decide free-ness themselves (e.g. the plan-benefit api,
  // which reads it off the response body).
  canRemoveEssentialPlan: (_, __, ___, rootGetters) => {
    return rootGetters['pricingLineup/isEssentialExcluded']
  },
  // Component-facing variant: the override only applies to free users, so fold
  // the free-plan check in here once instead of re-deriving it at every CTA.
  canRemoveEssentialPlanForFreeUser: (_, __, rootState, rootGetters) => {
    return rootGetters['pricingLineup/isEssentialExcluded']
      && rootState.plan?.group === 'free'
  },
  // AHA-39565: gate the email verification badge shown next to the email in
  // My profile > Personal info. Flag off restores the plain email text.
  canUseEmailVerificationComponent: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39565-release-email-verification-component')
  },
  // Gates the in-app header upgrade modal: on the `header-popover` or
  // `header-modal-yearly-only` variant, clicking "Upgrade" in the main header opens
  // HeaderUpgradeModal (Pro plan + pricing) instead of routing to the pricing page /
  // user-plan. Any other variant (or flag off) restores the original header upgrade
  // behaviour.
  canUseHeaderUpgradeModal: (_, getters) => {
    const variant = getters.getVariant('AHA-45905-release-header-upgrade-modal')?.name
    return variant === 'header-popover' || variant === 'header-modal-yearly-only'
  },
  // AHA-45905: on the `header-modal-yearly-only` variant the header upgrade modal drops
  // the Monthly billing option and offers only the Yearly plan. Any other variant (or
  // flag off) keeps both billing cycles.
  isHeaderUpgradeModalYearlyOnly: (_, getters) => {
    return getters.getVariant('AHA-45905-release-header-upgrade-modal')?.name === 'header-modal-yearly-only'
  },
  // BOO-180 / AHA-46496: variant gate for the editor side-nav custom-branding
  // upsell card. Only the `branding-banner-on` variant shows the card; flag off
  // or any other variant hides it (restores today's behaviour).
  isCustomBrandingBannerOn: (_, getters) => {
    return getters.getVariant(CUSTOM_BRANDING_BANNER_FLAG)?.name === 'branding-banner-on'
  },
  // PRO7-12: gate for the presenter-side tool bridge + /sandbox iframe host (the FAB
  // unified assistant). Also forceable locally via VUE_APP_FORCE_PRESENTER_TOOL_BRIDGE
  // for dev without an Unleash toggle. Off => the legacy Zoho / AI-chatbot path, byte-for-byte.
  canUsePresenterToolBridge: (_, getters) => {
    if (process.env.VUE_APP_FORCE_PRESENTER_TOOL_BRIDGE === 'true') return true
    return getters.isFeatureEnabled('release-PRO7-presenter-sandbox-tool-bridge')
  },
  // AHA-47691: which chatbot-app route the FAB assistant iframe loads. `treatment` =>
  // /assistant/ (the master agent: deck tools, file upload, Sonnet in the editor,
  // posting to the Worker's /chat/editor). Anything else => /sandbox/, byte-for-byte
  // today's panel. Variant-based so Mixpanel can tell "control" from "flag failed to load".
  canUseMasterAgentEditor: (_, getters) => {
    return getters.getVariant('AHA-47691-master-agent-editor')?.name === 'treatment'
  },
  // CUST-185: gates the share-link slug removal (PR #5323). ON = the new
  // slug-less link (`/share/<shareCode>`); OFF (safe default, flag not yet
  // created in Unleash) = the legacy slugged link
  // (`/share/<slugifiedName>-<shareCode>`). Lets us roll the change out
  // gradually / kill it instantly if a customer complains about the slug
  // disappearing.
  canRemoveSlugFromSharedLink: (_, getters) => getters.isFeatureEnabled('remove-slug-from-shared-link'),
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const getVariant = (rootGetters, featureName) => {
  return rootGetters['featureToggles/getVariant'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

function _handleResetFeatureFlagInitCoalescedPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const isExitingEditorScreen = fromRoute === ROUTE_NAMES.PRESENTATION_DETAIL
    && toRoute !== ROUTE_NAMES.PRESENTATION_DETAIL

  const isExitingSharingView = fromRoute === ROUTE_NAMES.PRESENTATION_SHARE
    && toRoute !== ROUTE_NAMES.PRESENTATION_SHARE

  // Applied routes
  const isExistingAppliedRoute = isExitingEditorScreen || isExitingSharingView

  // Check if the presentation is shared or owned
  const isOwnedPresentation = _store?.state?.presentation?.userId === _store?.state?.currentUser?.id

  if (isExistingAppliedRoute && !isOwnedPresentation) {
    // Reset the User Flag using current user context after existing the Shared Route
    _store.commit('featureToggles/setFeatureFlagInitCoalesced', null)
    _store.dispatch('featureToggles/initializeUserFeatureContext', {
      currentUser: _store?.state?.currentUser,
      presenterLanguage: _store?.state?.presenterLocale,
    })
  }
}

function _handleGetCurrentUserPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const fromPublicRoute = PUBLIC_ROUTES.includes(fromRoute)
  const toAuthGuardRoute = !PUBLIC_ROUTES.includes(toRoute)

  if (fromPublicRoute && toAuthGuardRoute) {
    _store.dispatch('getCurrentUserAction')
  }
}

const resetFeatureFlagInitCoalescedPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Handle the Reset Feature Flag after existing the Shared Route
    _handleResetFeatureFlagInitCoalescedPlugin(_store, action)
  })
}

const getCurrentUserPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Because getCurrentUserAction is not trigger inside login or signUp component but via child component
    // So there are nothing registered (mixpanel, sentry, ...)
    // We need to manually trigger the getCurrentUserAction
    _handleGetCurrentUserPlugin(_store, action)
  })
}

const plugins = [
  resetFeatureFlagInitCoalescedPlugin,
  getCurrentUserPlugin,
]

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
  plugins,
}
